import { Alert, Button, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import {
  RefundRow,
  RefundStatus,
  attachSalesOrderRefundBank,
  fetchSalesOrderRefunds,
} from "../../cash-log/refunds.api";
import { AttachBankModal, RefundPayToCell, isOpenRefund, needsBank } from "../refund/components/refund_bank";
import DetailSection from "./detail_section";

const STATUS_TAG: Record<RefundStatus, { color: string; label: string }> = {
  pending: { color: "orange", label: "Awaiting payout" },
  partial: { color: "gold", label: "Part paid" },
  partially_settled: { color: "gold", label: "Part paid" },
  settled: { color: "green", label: "Paid" },
  failed: { color: "red", label: "Failed" },
  released: { color: "default", label: "Back to wallet" },
};

const rupees = (v: string | number) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OrderRefundsSection: React.FC<{
  salesOrderId: number;
  canEdit: boolean;
  reloadKey?: unknown;
}> = ({ salesOrderId, canEdit, reloadKey }) => {
  const [rows, setRows] = useState<RefundRow[] | null>(null);
  const [attaching, setAttaching] = useState<RefundRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSalesOrderRefunds(salesOrderId).then((data) => {
      if (!cancelled) setRows(data);
    });
    return () => {
      cancelled = true;
    };
  }, [salesOrderId, reloadKey]);

  if (!rows?.length) return null;

  const missing = rows.filter(needsBank).length;
  const replaceRow = (next: RefundRow) =>
    setRows((prev) => (prev || []).map((r) => (r.id === next.id ? next : r)));

  const columns: ColumnsType<RefundRow> = [
    {
      title: "Refund",
      key: "refund",
      render: (_, r) => (
        <div style={{ lineHeight: "20px" }}>
          <Typography.Text strong>#{r.id}</Typography.Text>
          <div style={{ color: "#8c8c8c", fontSize: 12 }}>
            {r.cn_number || "—"} · {dayjs(r.created_at).format("DD MMM YYYY")}
          </div>
        </div>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, r) => (
        <div style={{ lineHeight: "20px", whiteSpace: "nowrap" }}>
          <div>{rupees(r.payout_rupees)}</div>
          {Number(r.settled_rupees) > 0 && (
            <div style={{ color: "#8c8c8c", fontSize: 12 }}>{rupees(r.settled_rupees)} paid</div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s: RefundStatus) => (
        <Tag color={STATUS_TAG[s]?.color}>{STATUS_TAG[s]?.label || s}</Tag>
      ),
    },
    {
      title: "Pay to",
      key: "bank",
      render: (_, r) => (
        <RefundPayToCell row={r} onAttach={canEdit ? () => setAttaching(r) : undefined} />
      ),
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, r) =>
        canEdit && isOpenRefund(r.status) ? (
          <Button
            size="small"
            type={needsBank(r) ? "primary" : "default"}
            onClick={() => setAttaching(r)}
          >
            {r.destination?.bank_details && r.destination.bank_source === "refund"
              ? "Change bank"
              : "Add bank"}
          </Button>
        ) : null,
    },
  ];

  return (
    <DetailSection
      sectionKey="refunds"
      title="Customer refunds"
      defaultOpen={missing > 0}
      summary={
        <>
          <Tag bordered={false}>
            {rows.length} refund{rows.length === 1 ? "" : "s"}
          </Tag>
          {missing > 0 && (
            <Tag color="red" bordered={false}>
              {missing} need{missing === 1 ? "s" : ""} bank details
            </Tag>
          )}
        </>
      }
    >
      {missing > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Accounts can't pay these refunds until the customer's bank account is confirmed."
          description="Get the account details from the customer, then use Add bank to attach them. Accounts pays to whatever is attached here."
        />
      )}
      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={rows} />
      <AttachBankModal
        row={attaching}
        attach={(r, bankId) => attachSalesOrderRefundBank(salesOrderId, r.id, bankId)}
        onClose={() => setAttaching(null)}
        onDone={replaceRow}
      />
    </DetailSection>
  );
};

export default OrderRefundsSection;
